<script setup>
import { ref, computed, watch } from 'vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import NextInput from 'dashboard/components-next/input/Input.vue';
import { useAlert } from 'dashboard/composables';
import GoldmanService from 'dashboard/api/integrations/goldman';
import HobbyService from 'dashboard/api/integrations/hobby';

const firstPresent = (...values) =>
  values.find(value => value !== undefined && value !== null && value !== '');

const firstDefined = (...values) =>
  values.find(value => value !== undefined && value !== null);

const props = defineProps({
  conversation: {
    type: Object,
    required: true,
  },
  contact: {
    type: Object,
    required: true,
  },
});

const user = ref(null);
const loading = ref(false);
const showExtend = ref(false);
const showAdjust = ref(false);
const showPayments = ref(false);
const subMonths = ref(1);
const adjustDaysCount = ref(0);

const contactId = computed(() => props.contact?.id);
const contactAdditionalAttributes = computed(
  () => props.contact?.additional_attributes || {}
);
const contactCustomAttributes = computed(
  () => props.contact?.custom_attributes || {}
);
const conversationCustomAttributes = computed(
  () => props.conversation?.custom_attributes || {}
);
const serviceOrigin = computed(
  () =>
    conversationCustomAttributes.value.service_origin ||
    contactAdditionalAttributes.value.service_origin ||
    contactCustomAttributes.value.service_origin ||
    null
);
const isHobbyContext = computed(() => {
  const identifier = props.contact?.identifier || '';

  return (
    serviceOrigin.value === 'hobbyvps_online' ||
    identifier.startsWith('hobbyvps:user:') ||
    identifier.startsWith('hobbyvps:visitor:')
  );
});
const supportBrandName = computed(() =>
  isHobbyContext.value ? 'Hobby VPN' : 'Goldman VPN'
);
const supportService = computed(() =>
  (isHobbyContext.value ? HobbyService : GoldmanService)
);

const telegramId = computed(() => {
  if (!props.contact) return null;

  const attrs = contactAdditionalAttributes.value;
  const customAttrs = contactCustomAttributes.value;

  return (
    attrs.social_telegram_user_id ||
    (attrs.social_profiles && attrs.social_profiles[0]?.id) ||
    attrs.telegram_id ||
    customAttrs.telegram_id ||
    props.contact.identifier
  );
});

const legacyUser = computed(() => {
  if (!user.value) return null;

  if (user.value.legacy_user) {
    return user.value.legacy_user;
  }

  if (
    user.value.telegram_id &&
    (user.value.subscription || user.value.payments || user.value.balance !== undefined)
  ) {
    return user.value;
  }

  return null;
});

const supportSnapshot = computed(
  () => user.value?.support_snapshot || user.value?.site_context?.snapshot || null
);
const siteProfile = computed(() => supportSnapshot.value?.profile || {});
const siteSubscriptionSummary = computed(
  () => supportSnapshot.value?.subscriptionSummary || {}
);
const siteCurrentSubscription = computed(() => {
  if (supportSnapshot.value?.currentSubscription) {
    return supportSnapshot.value.currentSubscription;
  }

  const expireDate = firstPresent(
    contactCustomAttributes.value.expireDate,
    contactCustomAttributes.value.expire_date,
    siteSubscriptionSummary.value?.latestExpireDate
  );
  const hasSummary =
    expireDate ||
    siteSubscriptionSummary.value?.latestPlanType ||
    siteSubscriptionSummary.value?.active !== undefined;

  if (!hasSummary) {
    return null;
  }

  return {
    status:
      firstPresent(
        contactCustomAttributes.value.subscriptionStatus,
        contactCustomAttributes.value.subscription_status
      ) || (siteSubscriptionSummary.value?.active ? 'active' : 'inactive'),
    expireDate,
    autoRenew:
      firstDefined(
        contactCustomAttributes.value.autoRenew,
        contactCustomAttributes.value.auto_renew
      ) ?? false,
  };
});
const siteVpnConnection = computed(() => {
  if (supportSnapshot.value?.vpnConnection) {
    return supportSnapshot.value.vpnConnection;
  }

  return {
    vpnKey: firstPresent(
      supportSnapshot.value?.vpnKey,
      siteCurrentSubscription.value?.vpnKey,
      siteCurrentSubscription.value?.subscriptionUrl,
      contactCustomAttributes.value.subscriptionUrl,
      contactCustomAttributes.value.subscription_url
    ),
    subscriptionUrl: firstPresent(
      supportSnapshot.value?.subscriptionUrl,
      siteCurrentSubscription.value?.subscriptionUrl,
      contactCustomAttributes.value.subscriptionUrl,
      contactCustomAttributes.value.subscription_url
    ),
    expiresAt: firstPresent(
      supportSnapshot.value?.expiresAt,
      siteCurrentSubscription.value?.expireDate,
      contactCustomAttributes.value.expireDate,
      contactCustomAttributes.value.expire_date
    ),
    autoRenew:
      firstDefined(
        supportSnapshot.value?.autoRenew,
        siteCurrentSubscription.value?.autoRenew,
        contactCustomAttributes.value.autoRenew,
        contactCustomAttributes.value.auto_renew
      ) ?? false,
  };
});
const sitePaymentsSummary = computed(
  () => supportSnapshot.value?.payments?.summary || supportSnapshot.value?.paymentsSummary || {}
);
const sitePayments = computed(() => {
  const items = supportSnapshot.value?.payments?.items;
  if (!Array.isArray(items)) {
    return [];
  }

  return items.map(payment => ({
    id: payment.id,
    amount: payment.amount,
    created_at: payment.createdAt || payment.created_at || payment.updatedAt || payment.updated_at,
    status: String(payment.status || '').toUpperCase(),
  }));
});

const siteUserId = computed(() =>
  firstPresent(
    supportSnapshot.value?.identity?.siteUserId,
    supportSnapshot.value?.userId,
    user.value?.site_user_id,
    contactCustomAttributes.value.siteUserId,
    contactCustomAttributes.value.site_user_id,
    contactAdditionalAttributes.value.site_user_id,
    contactAdditionalAttributes.value.siteUserId
  )
);
const siteDisplayName = computed(
  () =>
    firstPresent(
      supportSnapshot.value?.identity?.displayName,
      supportSnapshot.value?.displayName,
      props.contact?.name,
      supportSnapshot.value?.identity?.email,
      supportSnapshot.value?.email,
      props.contact?.email,
      siteUserId.value ? `Пользователь сайта #${siteUserId.value}` : null
    ) || 'Пользователь сайта'
);
const siteUsername = computed(
  () =>
    firstPresent(
      contactAdditionalAttributes.value.social_telegram_user_name,
      contactAdditionalAttributes.value.username,
      contactCustomAttributes.value.telegram_username,
      contactCustomAttributes.value.telegramUsername,
      supportSnapshot.value?.identity?.telegramId,
      supportSnapshot.value?.telegramId,
      siteUserId.value ? `site-${siteUserId.value}` : null
    ) || 'site-user'
);
const siteBalance = computed(() => Number(siteProfile.value?.balance || 0));
const siteIsActive = computed(() => {
  const status = String(
    firstPresent(
      siteCurrentSubscription.value?.status,
      contactCustomAttributes.value.subscriptionStatus,
      contactCustomAttributes.value.subscription_status
    ) || ''
  ).toLowerCase();

  if (status) {
    return status === 'active';
  }

  return Boolean(siteSubscriptionSummary.value?.active);
});
const siteCreatedAt = computed(() =>
  firstPresent(siteProfile.value?.createdAt, siteProfile.value?.created_at)
);
const siteExpireDate = computed(() =>
  firstPresent(
    siteCurrentSubscription.value?.expireDate,
    siteCurrentSubscription.value?.expire_date,
    siteVpnConnection.value?.expiresAt
  )
);
const siteSubscriptionDaysLeft = computed(() => {
  if (!siteExpireDate.value) {
    return 0;
  }

  const expireAt = new Date(siteExpireDate.value);
  if (Number.isNaN(expireAt.getTime())) {
    return 0;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  expireAt.setHours(0, 0, 0, 0);

  return Math.max(
    0,
    Math.ceil((expireAt.getTime() - today.getTime()) / (24 * 60 * 60 * 1000))
  );
});
const siteSubscriptionUrl = computed(() =>
  firstPresent(
    siteVpnConnection.value?.subscriptionUrl,
    siteCurrentSubscription.value?.subscriptionUrl,
    contactCustomAttributes.value.subscriptionUrl,
    contactCustomAttributes.value.subscription_url
  )
);
const siteKeyValue = computed(() =>
  firstPresent(
    siteVpnConnection.value?.vpnKey,
    siteCurrentSubscription.value?.vpnKey,
    siteSubscriptionUrl.value
  )
);
const legacySubscriptionUrl = computed(() =>
  firstPresent(
    user.value?.subscription_url,
    user.value?.subscriptionUrl,
    legacyUser.value?.subscription_url,
    legacyUser.value?.subscriptionUrl,
    legacyUser.value?.subscription?.subscription_url,
    legacyUser.value?.subscription?.subscriptionUrl,
    siteKeyValue.value
  )
);
const legacySubUsername = computed(() =>
  firstPresent(
    user.value?.sub_username,
    user.value?.subUsername,
    legacyUser.value?.sub_username,
    legacyUser.value?.subUsername
  )
);
const siteTrialUsed = computed(() =>
  Boolean(supportSnapshot.value?.trial?.alreadyUsed)
);
const siteIsPartner = computed(() =>
  Boolean(
    firstDefined(siteProfile.value?.is_partner, siteProfile.value?.isPartner) ?? false
  )
);

const panelMode = computed(() => {
  if (legacyUser.value) {
    return 'legacy';
  }

  if (supportSnapshot.value) {
    return 'site';
  }

  return null;
});

const realTotalSpent = computed(() => {
  if (!legacyUser.value?.payments) return 0;

  return legacyUser.value.payments
    .filter(payment => payment.status === 'COMPLETED')
    .reduce((sum, payment) => sum + (parseFloat(payment.amount) || 0), 0);
});

const panelUser = computed(() => {
  if (panelMode.value === 'legacy' && legacyUser.value) {
    return {
      first_name: legacyUser.value.first_name || '',
      last_name: legacyUser.value.last_name || '',
      username: legacyUser.value.username || 'N/A',
      balance: legacyUser.value.balance || 0,
      is_active: Boolean(legacyUser.value.is_active),
      created_at: legacyUser.value.created_at,
      subscription: legacyUser.value.subscription || null,
      subscription_days_left:
        legacyUser.value.subscription_days_left ??
        legacyUser.value.subscription?.days_left ??
        0,
      subscription_url: legacySubscriptionUrl.value,
      sub_username: legacySubUsername.value,
      total_spent: realTotalSpent.value,
      is_partner: Boolean(legacyUser.value.is_partner),
      trial_used: Boolean(legacyUser.value.trial_used),
      key_activated: legacyUser.value.key_activated ?? Boolean(legacySubscriptionUrl.value),
      payments: Array.isArray(legacyUser.value.payments) ? legacyUser.value.payments : [],
    };
  }

  if (panelMode.value === 'site') {
    return {
      first_name: siteDisplayName.value,
      last_name: '',
      username: siteUsername.value,
      balance: siteBalance.value,
      is_active: siteIsActive.value,
      created_at: siteCreatedAt.value,
      subscription: siteCurrentSubscription.value
        ? {
            expire_date: siteExpireDate.value,
            auto_renew:
              firstDefined(
                siteCurrentSubscription.value.auto_renew,
                siteCurrentSubscription.value.autoRenew,
                siteVpnConnection.value?.autoRenew
              ) ?? false,
          }
        : null,
      subscription_days_left: siteSubscriptionDaysLeft.value,
      subscription_url: siteKeyValue.value,
      sub_username: null,
      total_spent: Number(sitePaymentsSummary.value?.totalSucceededAmount || 0),
      is_partner: siteIsPartner.value,
      trial_used: siteTrialUsed.value,
      key_activated: Boolean(siteKeyValue.value),
      payments: sitePayments.value,
    };
  }

  return null;
});

const hasAnyGoldmanData = computed(() => Boolean(panelUser.value));
const canManageSubscription = computed(
  () => panelMode.value === 'legacy' || (panelMode.value === 'site' && Boolean(siteUserId.value))
);

const fetchUser = async () => {
  if (!contactId.value) return;

  loading.value = true;
  user.value = null;
  try {
    const { data } = await supportService.value.getUser(contactId.value);
    if (data && Object.keys(data).length > 0) {
      user.value = data;
    }
  } catch (error) {
    console.error(`Error fetching ${supportBrandName.value} user`, error);
  } finally {
    loading.value = false;
  }
};

const formatDate = dateStr => {
  if (!dateStr) return '-';

  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) {
    return '-';
  }

  return date.toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

const formatCurrency = amount =>
  new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'RUB' }).format(
    amount || 0
  );

const copyKey = () => {
  if (!panelUser.value?.subscription_url) return;
  navigator.clipboard.writeText(panelUser.value.subscription_url);
  useAlert('Ключ скопирован');
};

const copySubLink = () => {
  if (!panelUser.value?.sub_username) return;
  const link = `https://admin.goldmanvpn.pro/sub/${panelUser.value.sub_username}`;
  navigator.clipboard.writeText(link);
  useAlert('Ссылка скопирована');
};

const addSubscription = async () => {
  if (!canManageSubscription.value) return;

  try {
    await supportService.value.addSubscription(contactId.value, subMonths.value);
    useAlert('Подписка добавлена');
    showExtend.value = false;
    fetchUser();
  } catch (error) {
    useAlert('Ошибка добавления подписки');
  }
};

const adjustDays = async () => {
  if (!canManageSubscription.value) return;

  try {
    await supportService.value.adjustSubscription(contactId.value, adjustDaysCount.value);
    useAlert('Срок подписки изменен');
    showAdjust.value = false;
    adjustDaysCount.value = 0;
    fetchUser();
  } catch (error) {
    useAlert('Ошибка изменения срока');
  }
};

const cancelSubscription = async () => {
  if (!canManageSubscription.value) return;
  if (!confirm('Вы уверены, что хотите отменить подписку?')) return;

  try {
    await supportService.value.cancelSubscription(contactId.value);
    useAlert('Подписка отменена');
    fetchUser();
  } catch (error) {
    useAlert('Ошибка отмены');
  }
};

const toggleAutoRenew = async () => {
  if (!canManageSubscription.value) return;

  try {
    await supportService.value.toggleAutoRenew(contactId.value);
    useAlert('Автопродление изменено');
    fetchUser();
  } catch (error) {
    useAlert('Ошибка изменения автопродления');
  }
};

watch(
  () => props.contact?.id,
  () => {
    fetchUser();
  },
  { immediate: true }
);
</script>

<template>
  <div class="h-full overflow-y-auto bg-slate-50 dark:bg-slate-900 border-l border-slate-200 dark:border-slate-800">
    <div v-if="loading" class="flex justify-center p-8">
      <div class="spinner text-slate-400 size-6 animate-spin rounded-full border-2 border-current border-t-transparent"></div>
    </div>

    <div v-else-if="!hasAnyGoldmanData" class="p-6 text-center text-slate-500 text-sm">
      Пользователь не найден в {{ supportBrandName }}
      <div class="mt-2 text-xs text-slate-400">ID: {{ telegramId || 'Не определен' }}</div>
      <NextButton class="mt-4" size="sm" variant="outline" label="Обновить" @click="fetchUser" />
    </div>

    <div v-else class="p-4 space-y-4">
      <div class="flex items-center gap-3 pb-4 border-b border-slate-200 dark:border-slate-800">
        <div class="size-10 rounded-full bg-slate-200 dark:bg-slate-800 flex items-center justify-center text-slate-500 font-bold text-lg">
          {{ panelUser.first_name?.[0] || 'U' }}
        </div>
        <div class="flex-1 min-w-0">
          <div class="font-semibold text-sm truncate">{{ panelUser.first_name }} {{ panelUser.last_name }}</div>
          <div class="text-xs text-slate-500">@{{ panelUser.username || 'N/A' }}</div>
        </div>
        <NextButton size="xs" variant="ghost" icon="i-ph-arrows-clockwise" @click="fetchUser" />
      </div>

      <div class="p-3 bg-white dark:bg-slate-900 rounded-md border border-slate-200 dark:border-slate-800 shadow-sm">
        <div class="flex justify-between items-baseline mb-2">
          <span class="text-xs text-slate-500 uppercase font-bold tracking-wide">Баланс</span>
          <span class="text-lg font-bold text-teal-600 dark:text-teal-400">{{ formatCurrency(panelUser.balance) }}</span>
        </div>
        <div class="grid grid-cols-2 gap-2 text-xs">
          <div>
            <span class="text-slate-400">Статус:</span>
            <span class="ml-1 font-medium" :class="panelUser.is_active ? 'text-teal-600' : 'text-red-500'">
              {{ panelUser.is_active ? 'Активен' : 'Неактивен' }}
            </span>
          </div>
          <div>
            <span class="text-slate-400">Регистрация:</span>
            <span class="ml-1 font-medium">{{ formatDate(panelUser.created_at) }}</span>
          </div>
        </div>
      </div>

      <div v-if="panelUser.subscription" class="p-3 bg-white dark:bg-slate-900 rounded-md border border-slate-200 dark:border-slate-800 shadow-sm">
        <div class="flex justify-between items-center mb-4 pb-2 border-b border-slate-100 dark:border-slate-800">
          <span class="text-xs font-bold text-slate-500 uppercase tracking-wide">Подписка</span>
          <button
            v-if="canManageSubscription"
            @click="toggleAutoRenew"
            class="text-xs font-medium flex items-center gap-1 hover:text-slate-900 dark:hover:text-slate-200 transition-colors"
          >
            <span class="text-slate-400">Авто:</span>
            <span :class="panelUser.subscription.auto_renew ? 'text-teal-600 dark:text-teal-400 font-bold' : 'text-slate-400'">
              {{ panelUser.subscription.auto_renew ? 'ВКЛ' : 'ВЫКЛ' }}
            </span>
          </button>
          <div v-else class="text-xs font-medium flex items-center gap-1">
            <span class="text-slate-400">Авто:</span>
            <span :class="panelUser.subscription.auto_renew ? 'text-teal-600 dark:text-teal-400 font-bold' : 'text-slate-400'">
              {{ panelUser.subscription.auto_renew ? 'ВКЛ' : 'ВЫКЛ' }}
            </span>
          </div>
        </div>

        <div class="grid grid-cols-2 gap-y-2 text-xs mb-4">
          <span class="text-slate-500">Истекает</span>
          <span class="text-right font-mono font-bold">{{ formatDate(panelUser.subscription.expire_date) }}</span>
          <span class="text-slate-500">Осталось</span>
          <span class="text-right font-bold text-teal-600 dark:text-teal-400">{{ panelUser.subscription_days_left || 0 }} дн.</span>
        </div>

        <div v-if="canManageSubscription" class="flex gap-2 mb-3">
          <NextButton class="flex-1" variant="solid" color="teal" size="sm" label="Продлить" @click="showExtend = !showExtend; showAdjust = false" />
          <NextButton class="flex-1" variant="outline" color="slate" size="sm" label="Коррекц." @click="showAdjust = !showAdjust; showExtend = false" />
        </div>

        <div v-if="canManageSubscription && showExtend" class="p-3 bg-slate-50 dark:bg-slate-800/50 rounded border border-slate-200 dark:border-slate-700 mb-3">
          <div class="text-xs mb-2 font-medium text-slate-600 dark:text-slate-400">Период (мес):</div>
          <div class="flex gap-2 mb-3">
            <button
              v-for="m in [1,3,6,12]"
              :key="m"
              @click="subMonths = m"
              class="flex-1 py-1.5 text-xs font-medium rounded border transition-all"
              :class="subMonths === m ? 'bg-teal-600 border-teal-600 text-white shadow-sm' : 'bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:border-slate-400'"
            >
              {{ m }}
            </button>
          </div>
          <NextButton block variant="solid" color="teal" size="sm" label="Подтвердить" @click="addSubscription" />
        </div>

        <div v-if="canManageSubscription && showAdjust" class="p-3 bg-slate-50 dark:bg-slate-800/50 rounded border border-slate-200 dark:border-slate-700 mb-3">
          <div class="text-xs mb-2 font-medium text-slate-600 dark:text-slate-400">Дни (+/-):</div>
          <NextInput type="number" v-model="adjustDaysCount" placeholder="0" class="mb-3" />
          <NextButton block variant="outline" color="slate" size="sm" label="Применить" @click="adjustDays" />
        </div>

        <div v-if="canManageSubscription" class="text-center pt-2 border-t border-slate-100 dark:border-slate-800">
          <NextButton
            variant="ghost"
            :color="panelUser.subscription.auto_renew ? 'ruby' : 'teal'"
            size="xs"
            :label="panelUser.subscription.auto_renew ? 'Отключить автопродление' : 'Включить автопродление'"
            @click="toggleAutoRenew"
          />
        </div>
      </div>

      <div v-else class="p-6 bg-slate-50 dark:bg-slate-900 rounded border border-dashed border-slate-300 dark:border-slate-700 text-center">
        <div class="text-sm text-slate-500 mb-3">Нет подписки</div>
        <NextButton
          v-if="canManageSubscription"
          variant="solid"
          color="teal"
          size="sm"
          icon="i-ph-plus"
          label="Создать"
          @click="showExtend = !showExtend"
        />

        <div v-if="canManageSubscription && showExtend" class="mt-4 text-left">
          <div class="flex gap-2 mb-3">
            <button
              v-for="m in [1,3,6,12]"
              :key="m"
              @click="subMonths = m"
              class="flex-1 py-1 text-xs rounded border"
              :class="subMonths === m ? 'bg-teal-600 text-white border-teal-600' : 'bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600'"
            >
              {{ m }}
            </button>
          </div>
          <NextButton block variant="solid" color="teal" size="sm" label="OK" @click="addSubscription" />
        </div>
      </div>

      <div v-if="panelUser.subscription" class="p-3 bg-white dark:bg-slate-900 rounded-md border border-slate-200 dark:border-slate-800 shadow-sm relative">
        <div class="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">VPN Ключ</div>
        <div class="flex gap-2 items-center">
          <input
            type="text"
            :value="panelUser.subscription_url || 'Ключ не доступен'"
            readonly
            class="flex-1 px-2 py-1.5 text-xs font-mono bg-slate-100 dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700 truncate text-slate-600 dark:text-slate-300 outline-none focus:border-teal-500 transition-colors"
            @click="$event.target.select()"
          />
          <button
            @click="copyKey"
            class="p-1.5 bg-teal-50 hover:bg-teal-100 dark:bg-teal-900/30 dark:hover:bg-teal-900/50 text-teal-600 dark:text-teal-400 rounded border border-teal-200 dark:border-teal-800 transition-colors flex-shrink-0"
            title="Копировать ключ"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
            </svg>
          </button>
        </div>
        <div v-if="panelUser.sub_username" class="mt-2">
          <div class="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">Ссылка подписки</div>
          <div class="flex gap-2 items-center">
            <a
              :href="`https://admin.goldmanvpn.pro/sub/${panelUser.sub_username}`"
              target="_blank"
              class="flex-1 px-2 py-1.5 text-xs font-mono bg-slate-100 dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700 truncate text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300 outline-none transition-colors"
            >
              {{ `https://admin.goldmanvpn.pro/sub/${panelUser.sub_username}` }}
            </a>
            <button
              @click="copySubLink"
              class="p-1.5 bg-teal-50 hover:bg-teal-100 dark:bg-teal-900/30 dark:hover:bg-teal-900/50 text-teal-600 dark:text-teal-400 rounded border border-teal-200 dark:border-teal-800 transition-colors flex-shrink-0"
              title="Копировать ссылку"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
              </svg>
            </button>
          </div>
        </div>
      </div>

      <div class="grid grid-cols-2 gap-3">
        <div class="stat-card">
          <div class="label">Потрачено</div>
          <div class="value">{{ formatCurrency(panelUser.total_spent) }}</div>
        </div>
        <div class="stat-card">
          <div class="label">Партнер</div>
          <div class="value" :class="panelUser.is_partner ? 'text-teal-600 dark:text-teal-400' : 'text-slate-400'">
            {{ panelUser.is_partner ? 'Да' : 'Нет' }}
          </div>
        </div>
        <div class="stat-card">
          <div class="label">Триал</div>
          <div class="value" :class="panelUser.trial_used ? 'text-slate-500' : 'text-teal-600 dark:text-teal-400'">
            {{ panelUser.trial_used ? 'Использован' : 'Доступен' }}
          </div>
        </div>
        <div class="stat-card">
          <div class="label">Ключ</div>
          <div class="value" :class="panelUser.key_activated ? 'text-teal-600 dark:text-teal-400' : 'text-amber-500'">
            {{ panelUser.key_activated ? 'Активирован' : 'Не активен' }}
          </div>
        </div>
      </div>

      <div v-if="panelUser.payments?.length">
        <button @click="showPayments = !showPayments" class="flex justify-between items-center w-full p-2 bg-slate-50 dark:bg-slate-900 rounded border border-slate-200 dark:border-slate-800 text-xs font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
          <span>История платежей ({{ panelUser.payments.length }})</span>
          <span :class="showPayments ? 'rotate-180' : ''" class="transition-transform text-[10px]">▼</span>
        </button>

        <div v-if="showPayments" class="mt-2 flex flex-col gap-1">
          <div v-for="payment in panelUser.payments.slice(0, 10)" :key="payment.id" class="flex justify-between items-center p-2 rounded bg-slate-50 dark:bg-slate-900/50 border border-slate-100 dark:border-slate-800 text-xs">
            <span class="font-mono text-slate-500">{{ formatDate(payment.created_at) }}</span>
            <div class="flex items-center gap-2">
              <span class="font-bold font-mono">{{ formatCurrency(payment.amount) }}</span>
              <div class="w-1.5 h-1.5 rounded-full" :class="payment.status === 'COMPLETED' || payment.status === 'SUCCEEDED' ? 'bg-teal-500' : 'bg-red-500'"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.stat-card {
  @apply p-2 rounded bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800;
}
.stat-card .label {
  @apply text-[10px] uppercase font-bold text-slate-400 mb-0.5;
}
.stat-card .value {
  @apply text-sm font-bold;
}
</style>
